import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import FlightRoundedIcon from '@mui/icons-material/FlightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LocalCafeRoundedIcon from '@mui/icons-material/LocalCafeRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SubscriptionsRoundedIcon from '@mui/icons-material/SubscriptionsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  createProjectVitalFinancePlan,
  getProjectVitalFinanceOverview,
  updateProjectVitalFinancePlan,
} from '../services/projectVitalService';
import type {
  ProjectVitalFinancePlan,
  ProjectVitalFinancePlanMovementType,
  ProjectVitalFinancePlanRecurrence,
  ProjectVitalFinancialScope,
} from '../types/projectVital';

type Props = {
  open: boolean;
  onClose: () => void;
  financialScope?: ProjectVitalFinancialScope;
  plan?: ProjectVitalFinancePlan | null;
};

type CategoryOption = {
  value: string;
  label: string;
  icon: ReactNode;
};

const EXPENSE_CATEGORIES: CategoryOption[] = [
  { value: 'Supermercado', label: 'Supermercado', icon: <ShoppingCartRoundedIcon /> },
  { value: 'Restaurantes', label: 'Restaurantes', icon: <RestaurantRoundedIcon /> },
  { value: 'Bares / cafeterías', label: 'Bares / cafés', icon: <LocalCafeRoundedIcon /> },
  { value: 'Gasolina', label: 'Gasolina', icon: <LocalGasStationRoundedIcon /> },
  { value: 'Transporte', label: 'Transporte', icon: <DirectionsCarRoundedIcon /> },
  { value: 'Vivienda', label: 'Vivienda', icon: <HomeRoundedIcon /> },
  { value: 'Facturas', label: 'Facturas', icon: <BoltRoundedIcon /> },
  { value: 'Suscripciones', label: 'Suscripciones', icon: <SubscriptionsRoundedIcon /> },
  { value: 'Compras', label: 'Compras', icon: <ShoppingBagRoundedIcon /> },
  { value: 'Viajes', label: 'Viajes', icon: <FlightRoundedIcon /> },
  { value: 'Trabajo / PROA', label: 'Trabajo / PROA', icon: <WorkRoundedIcon /> },
  { value: 'Impuestos y tasas', label: 'Impuestos', icon: <ReceiptLongRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

const INCOME_CATEGORIES: CategoryOption[] = [
  { value: 'Nómina', label: 'Nómina', icon: <WorkRoundedIcon /> },
  { value: 'Prestación', label: 'Prestación', icon: <PaymentsRoundedIcon /> },
  { value: 'PROA / clientes', label: 'PROA / clientes', icon: <StorefrontRoundedIcon /> },
  { value: 'Freelance', label: 'Freelance', icon: <WorkRoundedIcon /> },
  { value: 'Reembolso', label: 'Reembolso', icon: <PaymentsRoundedIcon /> },
  { value: 'Venta', label: 'Venta', icon: <ShoppingBagRoundedIcon /> },
  { value: 'Intereses / rendimientos', label: 'Intereses', icon: <TrendingUpRoundedIcon /> },
  { value: 'Ayuda / regalo', label: 'Ayuda / regalo', icon: <CardGiftcardRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

const RECURRENCES: Array<{
  value: ProjectVitalFinancePlanRecurrence;
  label: string;
  description: string;
}> = [
  {
    value: 'unico',
    label: 'Una vez',
    description: 'Solo en la fecha indicada',
  },
  {
    value: 'semanal',
    label: 'Semanal',
    description: 'Cada 7 días',
  },
  {
    value: 'mensual',
    label: 'Mensual',
    description: 'El mismo día de cada mes',
  },
  {
    value: 'anual',
    label: 'Anual',
    description: 'Una vez al año',
  },
];

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function tomorrowIso() {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  return value.toISOString().slice(0, 10);
}

function normalizeAmountInput(value: string) {
  const normalized = value
    .replace(/\./g, ',')
    .replace(/[^0-9,]/g, '');

  const [integerPart, ...decimalParts] = normalized.split(',');

  if (decimalParts.length === 0) {
    return integerPart;
  }

  return `${integerPart},${decimalParts.join('').slice(0, 2)}`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function ProjectVitalFinancePlanDialog({
  open,
  onClose,
  financialScope = 'personal',
  plan = null,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [movementType, setMovementType] =
    useState<ProjectVitalFinancePlanMovementType>('gasto');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [category, setCategory] = useState('Suscripciones');
  const [customCategory, setCustomCategory] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] =
    useState<ProjectVitalFinancePlanRecurrence>('mensual');
  const [startDate, setStartDate] = useState(tomorrowIso());
  const [endDate, setEndDate] = useState('');

  const overviewQuery = useQuery({
    queryKey: ['project-vital', 'finance', currentMonth()],
    queryFn: () => getProjectVitalFinanceOverview(currentMonth()),
    enabled: open,
  });

  const accounts = useMemo(
    () =>
      (overviewQuery.data?.accounts ?? []).filter(
        (account) => account.is_active,
      ),
    [overviewQuery.data],
  );

  const categoryOptions =
    movementType === 'ingreso'
      ? INCOME_CATEGORIES
      : EXPENSE_CATEGORIES;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (plan) {
      setName(plan.name);
      setMovementType(plan.movement_type);
      setAmount(String(plan.amount).replace('.', ','));
      setAccountId(plan.account_id);
      setDescription(plan.description || '');
      setRecurrence(plan.recurrence);
      setStartDate(plan.start_date);
      setEndDate(plan.end_date || '');

      const known = (
        plan.movement_type === 'ingreso'
          ? INCOME_CATEGORIES
          : EXPENSE_CATEGORIES
      ).map((item) => item.value);

      if (known.includes(plan.category)) {
        setCategory(plan.category);
        setCustomCategory('');
      } else {
        setCategory('Otros');
        setCustomCategory(plan.category);
      }

      return;
    }

    setName('');
    setMovementType('gasto');
    setAmount('');
    setAccountId('');
    setCategory('Suscripciones');
    setCustomCategory('');
    setDescription('');
    setRecurrence('mensual');
    setStartDate(tomorrowIso());
    setEndDate('');
  }, [open, plan]);

  useEffect(() => {
    if (plan) {
      return;
    }

    if (movementType === 'ingreso') {
      setCategory('Prestación');
      setCustomCategory('');
    } else {
      setCategory('Suscripciones');
      setCustomCategory('');
    }
  }, [movementType, plan]);

  useEffect(() => {
    if (
      open &&
      !plan &&
      accounts.length === 1 &&
      !accountId
    ) {
      setAccountId(accounts[0].id);
    }
  }, [open, plan, accounts, accountId]);

  const effectiveCategory =
    category === 'Otros'
      ? customCategory.trim()
      : category;

  const numericAmount = Number(amount.replace(',', '.'));

  const isValid =
    Boolean(name.trim()) &&
    !Number.isNaN(numericAmount) &&
    numericAmount > 0 &&
    Boolean(accountId) &&
    Boolean(effectiveCategory) &&
    Boolean(startDate) &&
    (
      !endDate ||
      endDate >= startDate
    );

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        movement_type: movementType,
        financial_scope: plan?.financial_scope ?? financialScope,
        amount: numericAmount,
        account_id: accountId,
        category: effectiveCategory,
        description: description.trim() || null,
        recurrence,
        start_date: startDate,
        end_date: endDate || null,
      };

      return plan
        ? updateProjectVitalFinancePlan(plan.id, payload)
        : createProjectVitalFinancePlan(payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-plans'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-forecast'],
        }),
      ]);

      enqueueSnackbar(
        plan
          ? 'Previsión actualizada.'
          : 'Previsión creada.',
        { variant: 'success' },
      );

      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.detail ||
          'No se pudo guardar la previsión.',
        { variant: 'error' },
      );
    },
  });

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#111c2f',
          border: fullScreen
            ? 'none'
            : '1px solid rgba(148,163,184,0.16)',
          borderRadius: fullScreen ? 0 : 3.5,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 850 }}>
        {plan ? 'Editar previsto' : 'Nuevo previsto'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.4} sx={{ pt: 1.5 }}>
          {!accounts.length && !overviewQuery.isLoading ? (
            <Alert severity="info">
              Primero necesitas una cuenta financiera activa.
            </Alert>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 0.8,
            }}
          >
            {[
              ['gasto', 'Gasto previsto'],
              ['ingreso', 'Ingreso previsto'],
            ].map(([value, label]) => {
              const selected = movementType === value;

              return (
                <ButtonBase
                  key={value}
                  onClick={() =>
                    setMovementType(
                      value as ProjectVitalFinancePlanMovementType,
                    )
                  }
                  sx={{
                    py: 1.3,
                    px: 1,
                    borderRadius: 2.5,
                    border: `1px solid ${
                      selected
                        ? alpha('#4DD0E1', 0.55)
                        : alpha('#94a3b8', 0.14)
                    }`,
                    bgcolor: selected
                      ? alpha('#4DD0E1', 0.10)
                      : alpha('#ffffff', 0.018),
                    color: selected ? '#4DD0E1' : '#94a3b8',
                    fontWeight: 800,
                  }}
                >
                  {label}
                </ButtonBase>
              );
            })}
          </Box>

          <TextField
            label="Nombre"
            placeholder="Ej. ChatGPT, prestación, seguro..."
            fullWidth
            autoFocus={!fullScreen}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <TextField
            label="Importe"
            type="text"
            fullWidth
            value={amount}
            onChange={(event) =>
              setAmount(normalizeAmountInput(event.target.value))
            }
            placeholder="0,00"
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[.,]?[0-9]{0,2}',
            }}
            sx={{
              '& input': {
                fontSize: '1.25rem',
                fontWeight: 850,
              },
            }}
          />

          <Box>
            <Typography
              sx={{
                mb: 1,
                color: '#94a3b8',
                fontSize: '0.78rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Cuenta
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 0.9,
              }}
            >
              {accounts.map((account) => {
                const selected = accountId === account.id;

                return (
                  <ButtonBase
                    key={account.id}
                    onClick={() => setAccountId(account.id)}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.3,
                        borderRadius: 2.5,
                        border: `1px solid ${
                          selected
                            ? alpha('#4DD0E1', 0.55)
                            : alpha('#94a3b8', 0.13)
                        }`,
                        bgcolor: selected
                          ? alpha('#4DD0E1', 0.09)
                          : alpha('#ffffff', 0.018),
                      }}
                    >
                      <Typography
                        sx={{
                          color: selected ? '#f8fafc' : '#cbd5e1',
                          fontWeight: 800,
                          fontSize: '0.84rem',
                        }}
                      >
                        {account.name}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.3,
                          color: '#64748b',
                          fontSize: '0.73rem',
                        }}
                      >
                        {formatCurrency(account.current_balance)}
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                mb: 1,
                color: '#94a3b8',
                fontSize: '0.78rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Categoría
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(3, minmax(0, 1fr))',
                  sm: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 0.8,
              }}
            >
              {categoryOptions.map((option) => {
                const selected = category === option.value;

                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => {
                      setCategory(option.value);

                      if (option.value !== 'Otros') {
                        setCustomCategory('');
                      }
                    }}
                    sx={{
                      display: 'block',
                      width: '100%',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        minHeight: 78,
                        height: '100%',
                        px: 0.8,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.65,
                        borderRadius: 2.5,
                        border: `1px solid ${
                          selected
                            ? alpha('#4DD0E1', 0.55)
                            : alpha('#94a3b8', 0.12)
                        }`,
                        bgcolor: selected
                          ? alpha('#4DD0E1', 0.09)
                          : alpha('#ffffff', 0.018),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          color: selected ? '#4DD0E1' : '#64748b',
                          '& svg': {
                            fontSize: 21,
                          },
                        }}
                      >
                        {option.icon}
                      </Box>

                      <Typography
                        sx={{
                          color: selected ? '#f8fafc' : '#cbd5e1',
                          fontWeight: 750,
                          fontSize: '0.68rem',
                          lineHeight: 1.15,
                          textAlign: 'center',
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>

            {category === 'Otros' ? (
              <TextField
                label="Nombre de la categoría"
                fullWidth
                value={customCategory}
                onChange={(event) =>
                  setCustomCategory(event.target.value)
                }
                sx={{ mt: 1.2 }}
              />
            ) : null}
          </Box>

          <Box>
            <Typography
              sx={{
                mb: 1,
                color: '#94a3b8',
                fontSize: '0.78rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Repetición
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 0.8,
              }}
            >
              {RECURRENCES.map((option) => {
                const selected = recurrence === option.value;

                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => setRecurrence(option.value)}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        p: 1.2,
                        borderRadius: 2.5,
                        border: `1px solid ${
                          selected
                            ? alpha('#4DD0E1', 0.55)
                            : alpha('#94a3b8', 0.12)
                        }`,
                        bgcolor: selected
                          ? alpha('#4DD0E1', 0.09)
                          : alpha('#ffffff', 0.018),
                      }}
                    >
                      <Typography
                        sx={{
                          color: selected ? '#f8fafc' : '#cbd5e1',
                          fontWeight: 800,
                          fontSize: '0.78rem',
                        }}
                      >
                        {option.label}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.25,
                          color: '#64748b',
                          fontSize: '0.65rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>

          <TextField
            label={
              recurrence === 'unico'
                ? 'Fecha prevista'
                : 'Primera fecha'
            }
            type="date"
            fullWidth
            value={startDate}
            onChange={(event) =>
              setStartDate(event.target.value)
            }
            InputLabelProps={{ shrink: true }}
          />

          {recurrence !== 'unico' ? (
            <TextField
              label="Finaliza el"
              type="date"
              fullWidth
              value={endDate}
              onChange={(event) =>
                setEndDate(event.target.value)
              }
              helperText="Opcional. Déjalo vacío si no tiene una fecha final."
              InputLabelProps={{ shrink: true }}
            />
          ) : null}

          <TextField
            label="Descripción"
            placeholder="Opcional"
            fullWidth
            value={description}
            onChange={(event) =>
              setDescription(event.target.value)
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pt: 2,
          pb: fullScreen
            ? 'calc(20px + env(safe-area-inset-bottom))'
            : 2.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !isValid}
        >
          {mutation.isPending
            ? 'Guardando...'
            : plan
              ? 'Guardar cambios'
              : 'Crear previsto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
